import { useState } from 'react';
import { User, Briefcase, Shield, UserCheck } from 'lucide-react';

const BACKEND_URL = window.location.origin;

/**
 * UserAvatar component that displays user's profile photo or a fallback icon
 * @param {Object} props
 * @param {string} props.src - Profile image URL
 * @param {string} props.name - User's name for alt text and initials fallback
 * @param {string} props.role - User's role for icon fallback (customer, provider, admin, manager)
 * @param {string} props.size - Size variant: 'xs', 'sm', 'md', 'lg' (default: 'md')
 * @param {string} props.className - Additional CSS classes
 */
export default function UserAvatar({ src, name, role, size = 'md', className = '' }) {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl'
  };
  
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };
  
  const getRoleIcon = () => {
    const iconClass = iconSizes[size];
    switch (role) {
      case 'provider':
        return <Briefcase className={`${iconClass} text-blue-500`} />;
      case 'admin':
        return <Shield className={`${iconClass} text-red-500`} />;
      case 'manager':
        return <UserCheck className={`${iconClass} text-purple-500`} />;
      default:
        return <User className={`${iconClass} text-slate-500`} />;
    }
  };
  
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  };
  
  const imageUrl = getImageUrl(src);
  
  // Show fallback if no image URL or if image failed to load
  if (!imageUrl || imageError) {
    return (
      <div className={`${sizeClasses[size]} bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 ${className}`}>
        {getRoleIcon()}
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0 ${className}`}>
      <img 
        src={imageUrl} 
        alt={name || 'User'} 
        className="w-full h-full object-cover"
        onError={() => setImageError(true)}
      />
    </div>
  );
}
